// src/app/shared/directives/word-limit-input.directive.ts
import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { countWords, truncateToWordLimit } from '../utils/word-limit.util';

export interface WordLimitTrimEvent {
  limit: number;
  source: 'type' | 'paste' | 'drop';
}

@Directive({
  selector: '[appWordLimit]',
  standalone: true
})
export class WordLimitInputDirective {
  @Input('appWordLimit') limit: number | null | undefined;
  @Output() limitTrimmed = new EventEmitter<WordLimitTrimEvent>();

  private composing = false;

  private hasAnnouncedLimit = false;

  constructor(private el: ElementRef<HTMLInputElement | HTMLTextAreaElement>) {}

  @HostListener('compositionstart')
  onCompositionStart(): void {
    this.composing = true;
  }

  @HostListener('compositionend')
  onCompositionEnd(): void {
    this.composing = false;
    this.clampCurrentValue();
  }

  @HostListener('beforeinput', ['$event'])
  onBeforeInput(event: InputEvent): void {
    if (this.composing) { return; } // let the IME finish its composition undisturbed
    const limit = this.limit;
    if (!limit) { return; }

    const inputType = event.inputType || '';
    // Never intercept deletion, selection, or undo/redo — only insertions can overflow.
    if (inputType.startsWith('delete') || inputType === 'historyUndo' || inputType === 'historyRedo') {
      return;
    }

    const el = this.el.nativeElement;
    const data = event.data ?? (event as any).dataTransfer?.getData('text') ?? '';
    if (!data) { return; }

    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    const isBulkInsert = inputType === 'insertFromPaste' || inputType === 'insertFromDrop';
    const source: WordLimitTrimEvent['source'] = inputType === 'insertFromDrop' ? 'drop' : (inputType === 'insertFromPaste' ? 'paste' : 'type');

    // --- Guard 1: "character stacking" at the cap ---
  
    const currentWordCount = countWords(el.value);
    if (currentWordCount >= limit) {
      const selectionLength = end - start;
      if (data.length > selectionLength) {
        event.preventDefault();
        if (isBulkInsert) {
          this.limitTrimmed.emit({ limit, source });
        } else if (!this.hasAnnouncedLimit) {
          this.hasAnnouncedLimit = true;
          this.limitTrimmed.emit({ limit, source });
        }
        return;
      }
    }

    // --- Guard 2: ordinary word-count overflow (new words pushing past the cap) ---
    const prospective = el.value.slice(0, start) + data + el.value.slice(end);
    const truncated = truncateToWordLimit(prospective, limit);

    if (truncated === prospective) { return; }
    event.preventDefault();
    if (isBulkInsert) {
      this.limitTrimmed.emit({ limit, source });
    } else if (!this.hasAnnouncedLimit && countWords(truncated) >= limit) {
      this.hasAnnouncedLimit = true;
      this.limitTrimmed.emit({ limit, source });
    }
    if (truncated === el.value) { return; } // already at the cap — nothing fits, no DOM churn, cursor stays put

    const cursor = truncated.length;
    el.value = truncated;

    el.dispatchEvent(new Event('input', { bubbles: true }));
    queueMicrotask(() => {
      try { el.setSelectionRange(cursor, cursor); } catch { /* non-text input types don't support selection */ }
    });
  }

 
  @HostListener('input')
  onInput(): void {
    if (this.composing) { return; }
    this.clampCurrentValue();
  }

  private clampCurrentValue(): void {
    const limit = this.limit;
    if (!limit) { return; }
    const el = this.el.nativeElement;
    const truncated = truncateToWordLimit(el.value, limit);
    if (truncated !== el.value) {
      const cursor = Math.min(el.selectionStart ?? truncated.length, truncated.length);
      el.value = truncated;
      el.dispatchEvent(new Event('input', { bubbles: true }));
      queueMicrotask(() => {
        try { el.setSelectionRange(cursor, cursor); } catch { /* ignore */ }
      });
    }
    // Keep the "already announced this streak" flag honest: once the
    // field is genuinely back under the cap (e.g. the user deleted text),
    // the next time it reaches the limit should be treated as a fresh
    // announcement, not silently suppressed by an old streak.
    this.hasAnnouncedLimit = countWords(el.value) >= limit;
  }
}